import api from 'api';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'components/modal';
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { ReportType, RootState } from 'types';
import { useParams } from 'react-router-dom';
import ReportList from './report-list/report-list';
import { toast } from 'react-toastify';
import GenerateForm from './generate-form/generate-form';
import './report-modal.scss';
import RequestLog from './request-log/request-log';
import ReportOptions from './report-options/report-options';
import { useDispatch, useSelector } from 'react-redux';
import { actions } from 'redux/reducers';
import ScheduleForm from './schedule-form/schedule-form';
import ScheduleList from './schedule-list/schedule-list';

function ReportModal({
    open,
    setOpen
} : {
    open: boolean,
    setOpen: Dispatch<SetStateAction<boolean>>
}) {
    const params: any = useParams();
    const dispatch = useDispatch();
    const state = useSelector((state: RootState) => state);
    const [section, setSection] = useState({
        previous: 1,
        current: 1
    });
    const [selectedReport, setSelectedReport] = useState<ReportType>();

    const updateSection = (newSection: number) => {
        setSection({previous: section.current, current: newSection});
    }

    const getReportOptions = useCallback(() => {
        api.report.getReportOptions()
        .then(data => {
            dispatch(actions.reportOptions.set(data));
        })
    }, [dispatch])

    const getReports = useCallback(async () => {
        await api.report.getClientReports(params.clientId)
        .then((data : ReportType[]) => {
            dispatch(actions.reports.set(data.filter(x => x.BinIds.includes(Number(state.selectedBin)))));
        })
        updateSection(SECTION_REPORT_LIST);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.clientId, state.selectedBin, dispatch])

    const getClientContacts = useCallback(async () => {
        await api.clientContact.getAll(params.clientId)
        .then(data => {            
            dispatch(actions.clientContacts.set(data));
        });
    }, [params.clientId, dispatch])
    
    const createUpdateReport = (report: ReportType) => {
        setSelectedReport(report);
        var idx = state.reports.findIndex((x : ReportType) => x.Id === report.Id);
        const newReports = [...state.reports];
        if (idx === -1){
            newReports.splice(0,0, report);
        }
        else {
            newReports[idx] = report;
        }
        dispatch(actions.reports.set(newReports));
    }

    function deleteReport(id: number) {
        api.report.delete(id)
        .then(data => {
            toast.success("Successfully Deleted");
        })
        .catch(err => {
            toast.error(err.toString());
        })
        .finally(
            () => getReports()
        )
    }
    
    function selectReport(id: number) {
        var report = state.reports.find(x => x.Id === id);

        if (report){
            setSelectedReport(report);
            updateSection(SECTION_REPORT_GENERATE);
        }
        // console.log(report);
    }
    
    const done = () => {
        setOpen(false);
        setTimeout(() => {
            updateSection(SECTION_REPORT_LIST);
        }, 200)
    }
    
    useEffect(() => {   
        if (params.clientId) {
            getReportOptions();
            getClientContacts();
            getReports();
        }
    }, [getReportOptions, getClientContacts, getReports, params.clientId]);

    return (
        <Modal id="report-modal" open={open} setOpen={setOpen}>
            <ModalHeader>
                <i className="fas fa-angle-double-left fa-2x" onClick={() => updateSection(section.previous)}></i>
                <p>{getSectionName(section.current)}</p>
            </ModalHeader>
            <ModalBody>
                {section.current === SECTION_REPORT_LIST && (
                    <div className="saved-reports">
                        <ReportList className={'report-list'}
                            selectCallback={selectReport}
                            setSection={updateSection}
                            deleteSubmit={deleteReport}
                            />
                        <button className='p-50' onClick={() => {setSelectedReport(undefined); updateSection(SECTION_REPORT_OPTIONS)}}><i className="fas fa-plus"></i> New Report</button>
                    </div>
                )}
                {section.current === SECTION_REPORT_OPTIONS && (
                    <div className="report-options">
                        <ReportOptions 
                            report={selectedReport} 
                            setReport={createUpdateReport}
                            setSection={updateSection}
                            />
                    </div>
                )}
                {section.current === SECTION_REPORT_GENERATE && (
                    <div className="generate">
                        {selectedReport && <GenerateForm report={selectedReport} setSection={updateSection} />}
                    </div>
                )}
                {section.current === SECTION_SCHEDULE && (
                    <div className="schedule">
                        {selectedReport && <ScheduleForm report={selectedReport} setSection={updateSection}/>}
                    </div>
                )}
                {section.current === SECTION_SCHEDULE_LIST && (
                    <ScheduleList className={'schedule-list'}
                        selectCallback={selectReport}
                        setSection={updateSection}
                        deleteSubmit={deleteReport}
                        />
                )}
                {section.current === SECTION_REQUEST_LOG && (
                <div className="request-log">
                    <RequestLog selectReport={selectReport}/>
                </div>
                )}
            </ModalBody>
            <ModalFooter>
                <div className="button-group">
                    <button className="p-50" onClick={() => {
                        getReports();
                        updateSection(SECTION_REPORT_LIST);
                    }}>Saved Reports</button>
                    <button className="p-50" onClick={() => updateSection(SECTION_SCHEDULE_LIST)}>Schedule List</button>
                    <button className="p-50" onClick={() => updateSection(SECTION_REQUEST_LOG)}>Request Log</button>
                    <button className="p-50" onClick={done}>Done</button>
                </div>
            </ModalFooter>
        </Modal>
    )
}

export default ReportModal;

export const SECTION_REPORT_LIST = 1;
export const SECTION_REPORT_OPTIONS = 2;
export const SECTION_REPORT_GENERATE = 3;
export const SECTION_SCHEDULE = 4;
export const SECTION_SCHEDULE_LIST = 5;
export const SECTION_REQUEST_LOG = 6;
    
const getSectionName = (id: number) => {
    return (
    {
        1: "Saved Reports",
        2: "Report Options",
        3: "Generate Report",
        4: "Add Schedule",
        5: "Schedule List",
        6: "Request Log"
    }[id] || ""
    );
};

export function timeSince(date : Date) {
    const SECONDS_YEAR = 31536000;
    const SECONDS_MONTH = 2592000;
    const SECONDS_DAY = 86400;
    var seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  
    var interval = seconds / SECONDS_YEAR;
  
    if (interval > 1) {
        if (Math.floor(interval) === 1){
            return "Last year";
        }
      return Math.floor(interval) + " years ago";
    }
    interval = seconds / SECONDS_MONTH;
    if (interval > 1) {
        if (Math.floor(interval) === 1){
            return "Last month";
        }
      return Math.floor(interval) + " months ago";
    }
    interval = seconds / SECONDS_DAY;
    if (interval > 1) {
        if (Math.floor(interval) === 1){
            return "Yesterday";
        }
        return Math.floor(interval) + " days ago";
    }
    return "Today";
}
